import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import { PDFDocument } from "pdf-lib";
import Dropzone from "./Dropzone";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

// Convertit un dataURL (png) en Uint8Array
function dataUrlToBytes(dataUrl) {
  const base64 = dataUrl.split(",")[1];
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export default function PdfSignAnnotator() {
  // PDF
  const [file, setFile] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [renderScale, setRenderScale] = useState(1.2);

  // Preview image
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);
  const pdfRef = useRef(null);

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Signature pad
  const [sigDataUrl, setSigDataUrl] = useState(null);
  const [sigColor, setSigColor] = useState("#111111");
  const [sigWidth, setSigWidth] = useState(3);

  // Placement overlay (sur l’aperçu)
  // en pixels (coordonnées CSS de l’image preview)
  const [sigRect, setSigRect] = useState({ x: 40, y: 60, w: 180, h: 70 });
  const [sigOpacity, setSigOpacity] = useState(1);

  // dimension du conteneur preview (pour convertir en coords PDF)
  const previewBoxRef = useRef(null);
  const [previewBox, setPreviewBox] = useState({ w: 1, h: 1 });

  // drag/resize state
  const dragRef = useRef({ mode: null, startX: 0, startY: 0, startRect: null });

  const cleanupPreviewUrl = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
  };

  const onPickPdf = (f) => {
    cleanupPreviewUrl();
    pdfRef.current = null;
    setFile(f || null);
    setPageCount(null);
    setPageNum(1);
  };

  // Observe la taille du container preview (pour conversion coord)
  useEffect(() => {
    if (!previewBoxRef.current) return;
    const el = previewBoxRef.current;

    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setPreviewBox({ w: Math.max(1, r.width), h: Math.max(1, r.height) });
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  // Charge PDF + num pages
  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!file) return;
      setLoadingPreview(true);

      try {
        const buf = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;

        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        setPageNum(1);
      } catch (e) {
        console.error(e);
        alert("Impossible de lire ce PDF.");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [file]);

  // Render preview pageNum
  useEffect(() => {
    let cancelled = false;

    async function render() {
      if (!file || !pdfRef.current || typeof pageCount !== "number") return;

      const safePage = clamp(pageNum, 1, pageCount);
      setLoadingPreview(true);

      try {
        const pdf = pdfRef.current;
        const page = await pdf.getPage(safePage);
        const viewport = page.getViewport({ scale: renderScale });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { alpha: false });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({ canvasContext: ctx, viewport }).promise;

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) =>
              b ? resolve(b) : reject(new Error("toBlob preview échoué")),
            "image/jpeg",
            0.85
          );
        });

        if (cancelled) return;

        const url = URL.createObjectURL(blob);
        cleanupPreviewUrl();
        previewUrlRef.current = url;
        setPreviewUrl(url);

        // reset placement (optionnel)
        setSigRect({ x: 40, y: 60, w: 180, h: 70 });
        if (safePage !== pageNum) setPageNum(safePage);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }

    render();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, renderScale, pageCount, file]);

  useEffect(() => {
    return () => cleanupPreviewUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Signature pad canvas ---
  const padRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const clearSig = () => {
    const c = padRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setSigDataUrl(null);
  };

  const saveSig = () => {
    const c = padRef.current;
    if (!c) return;
    const url = c.toDataURL("image/png");
    setSigDataUrl(url);
  };

  const padPointerDown = (e) => {
    const c = padRef.current;
    if (!c) return;

    drawingRef.current = true;
    c.setPointerCapture(e.pointerId);

    const r = c.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    lastRef.current = { x, y };

    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = sigColor;
    ctx.lineWidth = sigWidth;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const padPointerMove = (e) => {
    if (!drawingRef.current) return;
    const c = padRef.current;
    if (!c) return;

    const r = c.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;

    const ctx = c.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
  };

  const padPointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;

    const c = padRef.current;
    if (c) c.releasePointerCapture(e.pointerId);

    // auto save après dessin (pratique)
    saveSig();
  };

  // --- Overlay drag/resize sur la preview ---
  const startDrag = (mode, e) => {
    if (!sigDataUrl) return;
    e.preventDefault();
    e.stopPropagation();

    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startRect: { ...sigRect },
    };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", endDrag, { once: true });
  };

  const onDragMove = (e) => {
    const st = dragRef.current;
    if (!st.mode || !st.startRect) return;

    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;

    setSigRect((prev) => {
      const r0 = st.startRect;

      if (st.mode === "move") {
        return {
          ...prev,
          x: clamp(r0.x + dx, 0, previewBox.w - r0.w),
          y: clamp(r0.y + dy, 0, previewBox.h - r0.h),
          w: r0.w,
          h: r0.h,
        };
      }

      // resize (coin bas droit)
      if (st.mode === "resize") {
        const nw = clamp(r0.w + dx, 60, previewBox.w - r0.x);
        const nh = clamp(r0.h + dy, 30, previewBox.h - r0.y);
        return { ...prev, x: r0.x, y: r0.y, w: nw, h: nh };
      }

      return prev;
    });
  };

  const endDrag = () => {
    dragRef.current = { mode: null, startX: 0, startY: 0, startRect: null };
    window.removeEventListener("pointermove", onDragMove);
  };

  const canSavePdf = useMemo(() => {
    return !!file && !!sigDataUrl && !!previewUrl && !saving;
  }, [file, sigDataUrl, previewUrl, saving]);

  // --- Export PDF signé (pdf-lib) ---
  const downloadSignedPdf = async () => {
    if (!file || !sigDataUrl) return;
    if (!previewBoxRef.current) return;

    setSaving(true);
    try {
      const inputBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(inputBytes);

      const pages = pdfDoc.getPages();
      const targetIndex = clamp(pageNum, 1, pages.length) - 1;
      const page = pages[targetIndex];

      // Dimensions PDF (points)
      const pdfW = page.getWidth();
      const pdfH = page.getHeight();

      // Convert overlay coords (pixels sur preview) -> coords PDF (points)
      // previewBox = taille CSS de l’aperçu (zone où la signature se place)
      const sx = sigRect.x / previewBox.w;
      const sy = sigRect.y / previewBox.h;
      const sw = sigRect.w / previewBox.w;
      const sh = sigRect.h / previewBox.h;

      const drawW = sw * pdfW;
      const drawH = sh * pdfH;

      // IMPORTANT : axe Y inversé (CSS top->down, PDF bottom->up)
      const drawX = sx * pdfW;
      const drawY = pdfH - sy * pdfH - drawH;

      // Embed signature PNG
      const sigBytes = dataUrlToBytes(sigDataUrl);
      const png = await pdfDoc.embedPng(sigBytes);

      page.drawImage(png, {
        x: drawX,
        y: drawY,
        width: drawW,
        height: drawH,
        opacity: sigOpacity,
      });

      const outBytes = await pdfDoc.save();
      const blob = new Blob([outBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName(file.name)}-signe.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la génération du PDF signé.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>Signer / Annoter un PDF</h2>
      <small>Tout se fait dans votre navigateur (aucun envoi)</small>

      <Dropzone
        accept="application/pdf,.pdf"
        multiple={false}
        disabled={saving}
        acceptText="Dépose ton PDF ici"
        helperText="Glisse-dépose ou clique pour choisir"
        onFiles={(files) => onPickPdf(files?.[0] || null)}
      />

      {/* Signature pad */}
      <div
        style={{
          maxWidth: 900,
          margin: "14px auto 0",
          background: "#fff",
          border: "1px solid #e6e6e6",
          borderRadius: 14,
          padding: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 800, color: "#222" }}>Votre signature</div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#666",
              fontSize: 13,
            }}
          >
            Couleur
            <input
              type="color"
              value={sigColor}
              onChange={(e) => setSigColor(e.target.value)}
            />
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#666",
              fontSize: 13,
            }}
          >
            Épaisseur
            <input
              type="range"
              min="2"
              max="8"
              step="1"
              value={sigWidth}
              onChange={(e) => setSigWidth(Number(e.target.value))}
            />
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#666",
              fontSize: 13,
            }}
          >
            Opacité
            <input
              type="range"
              min="0.2"
              max="1"
              step="0.05"
              value={sigOpacity}
              onChange={(e) => setSigOpacity(Number(e.target.value))}
            />
          </label>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button type="button" onClick={clearSig}>
              Effacer
            </button>
            <button type="button" onClick={saveSig}>
              Valider
            </button>
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <canvas
            ref={padRef}
            width={820}
            height={200}
            onPointerDown={padPointerDown}
            onPointerMove={padPointerMove}
            onPointerUp={padPointerUp}
            style={{
              width: "100%",
              height: 200,
              borderRadius: 12,
              background: "#fafafa",
              border: "1px solid #e6e6e6",
              touchAction: "none", // important mobile
              cursor: "crosshair",
            }}
          />
          <p style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
            Dessine ta signature, puis “Valider”. (Elle sera placée sur la page)
          </p>
        </div>

        {sigDataUrl && (
          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: 13, color: "#666" }}>
              Aperçu signature :
            </div>
            <img
              src={sigDataUrl}
              alt="Signature"
              style={{
                height: 44,
                border: "1px solid #e6e6e6",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          </div>
        )}
      </div>

      {/* Preview PDF + placement */}
      <div style={{ maxWidth: 900, margin: "14px auto 0" }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 800, color: "#222" }}>Aperçu PDF</div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label style={{ color: "#666", fontSize: 13 }}>
              Qualité aperçu
              <select
                value={renderScale}
                onChange={(e) => setRenderScale(Number(e.target.value))}
                disabled={!file}
                style={{
                  marginLeft: 8,
                  padding: 8,
                  borderRadius: 10,
                  border: "1px solid #ccc",
                }}
              >
                <option value={1}>Standard</option>
                <option value={1.2}>Moyen</option>
                <option value={1.6}>Haute</option>
              </select>
            </label>

            {typeof pageCount === "number" && (
              <label style={{ color: "#666", fontSize: 13 }}>
                Page
                <input
                  type="number"
                  min={1}
                  max={pageCount}
                  value={pageNum}
                  onChange={(e) => setPageNum(Number(e.target.value))}
                  style={{
                    marginLeft: 8,
                    width: 80,
                    padding: 8,
                    borderRadius: 10,
                    border: "1px solid #ccc",
                  }}
                />
                <span style={{ marginLeft: 6 }}>/ {pageCount}</span>
              </label>
            )}
          </div>
        </div>

        <div
          ref={previewBoxRef}
          style={{
            marginTop: 10,
            position: "relative",
            width: "100%",
            background: "#fff",
            border: "1px solid #e6e6e6",
            borderRadius: 14,
            overflow: "hidden",
            minHeight: 280,
          }}
        >
          {loadingPreview && (
            <div style={{ padding: 16, color: "#666", textAlign: "center" }}>
              Chargement de l’aperçu…
            </div>
          )}

          {previewUrl && (
            <img
              src={previewUrl}
              alt={`PDF page ${pageNum}`}
              style={{ width: "100%", display: "block", userSelect: "none" }}
              draggable={false}
            />
          )}

          {/* Overlay signature */}
          {sigDataUrl && previewUrl && (
            <div
              onPointerDown={(e) => startDrag("move", e)}
              style={{
                position: "absolute",
                left: sigRect.x,
                top: sigRect.y,
                width: sigRect.w,
                height: sigRect.h,
                border: "2px solid #4caf50",
                borderRadius: 10,
                background: "rgba(76,175,80,0.08)",
                cursor: "move",
                touchAction: "none",
              }}
              title="Glisse pour déplacer"
            >
              <img
                src={sigDataUrl}
                alt="Signature overlay"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  opacity: sigOpacity,
                  pointerEvents: "none",
                }}
                draggable={false}
              />

              {/* poignée resize */}
              <div
                onPointerDown={(e) => startDrag("resize", e)}
                style={{
                  position: "absolute",
                  right: 2,
                  bottom: 2,
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  background: "#4caf50",
                  cursor: "nwse-resize",
                }}
                title="Redimensionner"
              />
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 14,
          }}
        >
          <button
            onClick={downloadSignedPdf}
            disabled={!canSavePdf}
            style={{ minWidth: 260 }}
          >
            {saving ? "Génération..." : "Télécharger le PDF signé"}
          </button>

          <button
            type="button"
            onClick={() => setSigRect({ x: 40, y: 60, w: 180, h: 70 })}
            disabled={!sigDataUrl || saving}
            style={{ minWidth: 180 }}
          >
            Réinitialiser la position
          </button>
        </div>

        <p
          style={{
            textAlign: "center",
            color: "#666",
            fontSize: 12,
            marginTop: 10,
          }}
        >
          Astuce : déplace la signature en glissant, redimensionne avec le coin
          vert.
        </p>
      </div>
    </section>
  );
}
